import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner, Alert } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const NoteItem = ({
    note,
    id,
    userId,
    editMode,
    deleteMode,
    noteLoading,
    onEnableEditMode,
    onExitEditMode,
    onEnableDeleteMode,
    onExitDeleteMode,
    onUpdateNote,
    onDeleteNote,
    onChangeNote,
    formatDate
}) => {
    const [editorContent, setEditorContent] = useState(note.noteText);
    const [showEmptyNoteAlert, setShowEmptyNoteAlert] = useState(false);
    const quillRef = useRef(null);

    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['clean']
        ]
    };

    const formats = [
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet'
    ];

    useEffect(() => {
        if (editMode[id]) {
            setEditorContent(note.noteText);
            setShowEmptyNoteAlert(false);
        }
    }, [editMode, id, note.noteText]);

    const handleEditorChange = useCallback((content) => {
        setEditorContent(content);
        setShowEmptyNoteAlert(false);
    }, []);

    const handleSaveNote = () => {
        // Remove HTML tags and check if content is empty
        const plainText = editorContent.replace(/<[^>]*>/g, '').trim();
        if (!plainText) {
            setShowEmptyNoteAlert(true);
            return;
        }
        onChangeNote(editorContent, id);
        onUpdateNote(id, editorContent);
    };

    return (
        <div className={`note-item ${userId === note.userId ? 'own-note' : ''}`}>
            {showEmptyNoteAlert && (
                <Alert variant="danger" onClose={() => setShowEmptyNoteAlert(false)} dismissible>
                    Cannot save an empty note. Please add some content before saving.
                </Alert>
            )}
            <div className="note-header">
                <div className="user-avatar">
                    <i className="fa fa-user-circle"></i>
                </div>
                <div className="note-meta">
                    <span className="user-name">{note.userName}</span>
                    <span className="note-time">
                        {new Date(Number(note.createdOn)).toLocaleString()}
                        {note.modifiedOn && (
                            <>
                                {` (edited ${formatDate(note.modifiedOn)}`}
                                {note?.modifiedBy && note?.modifiedBy !== note.userName && ` by ${note?.modifiedBy}`}
                                {')'}
                            </>
                        )}
                    </span>
                </div>
            </div>
            <div className="note-content">
                {editMode[id] ? (
                    <div>
                        <ReactQuill
                            ref={quillRef}
                            className="old-note-quill"
                            theme="snow"
                            readOnly={userId !== note.userId}
                            value={editorContent}
                            onChange={handleEditorChange}
                            modules={modules}
                            formats={formats}
                        />
                    </div>
                ) : (
                    <div className="note-text" dangerouslySetInnerHTML={{ __html: note.noteText }} />
                )}
            </div>
            <div className="note-actions">
                {userId === note.userId && !editMode[id] && !deleteMode[id] && (
                    <>
                        <Button
                            className="action-btn"
                            variant="secondary"
                            size="sm"
                            onClick={() => onEnableEditMode(id)}
                            disabled={noteLoading[id]}
                        >
                            <i className="fa fa-edit"></i>Edit
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            className="action-btn"
                            onClick={() => onEnableDeleteMode(id)}
                            disabled={noteLoading[id]}
                        >
                            <i className="fa fa-trash"></i>Delete
                        </Button>
                    </>
                )}
                {editMode[id] && (
                    <>
                        <Button
                            variant="primary"
                            size="sm"
                            className="action-btn"
                            onClick={handleSaveNote}
                            disabled={noteLoading[id]}
                        >
                            {noteLoading[id] ? (
                                <Spinner
                                    as="span"
                                    animation="grow"
                                    size="sm"
                                    aria-hidden="true"
                                />
                            ) : (
                                <>
                                    <i className="fa fa-save"></i>Save
                                </>
                            )}
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            className="action-btn"
                            onClick={() => onExitEditMode(id)}
                            disabled={noteLoading[id]}
                        >
                            <i className="fa fa-times"></i>Cancel
                        </Button>
                    </>
                )}
                {deleteMode[id] && (
                    <>
                        <Button
                            variant="danger"
                            size="sm"
                            className="action-btn"
                            onClick={() => onDeleteNote(note.id)}
                            disabled={noteLoading[id]}
                        >
                            {noteLoading[id] ? (
                                <Spinner
                                    as="span"
                                    animation="grow"
                                    size="sm"
                                    aria-hidden="true"
                                />
                            ) : (
                                <>
                                    <i className="fa fa-check"></i> Confirm
                                </>
                            )}
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            className="action-btn"
                            onClick={() => onExitDeleteMode(id)}
                            disabled={noteLoading[id]}
                        >
                            <i className="fa fa-times"></i> Cancel
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

NoteItem.propTypes = {
    note: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        noteText: PropTypes.string.isRequired,
        userName: PropTypes.string.isRequired,
        userId: PropTypes.string.isRequired,
        createdOn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        modifiedOn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        modifiedBy: PropTypes.string
    }).isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    userId: PropTypes.string.isRequired,
    editMode: PropTypes.arrayOf(PropTypes.bool).isRequired,
    deleteMode: PropTypes.arrayOf(PropTypes.bool).isRequired,
    noteLoading: PropTypes.arrayOf(PropTypes.bool).isRequired,
    onEnableEditMode: PropTypes.func.isRequired,
    onExitEditMode: PropTypes.func.isRequired,
    onEnableDeleteMode: PropTypes.func.isRequired,
    onExitDeleteMode: PropTypes.func.isRequired,
    onUpdateNote: PropTypes.func.isRequired,
    onDeleteNote: PropTypes.func.isRequired,
    onChangeNote: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired
};

export default NoteItem; 